import { memo } from 'react';
import { Mail, TrendingUp, TrendingDown, UserMinus } from 'lucide-react';

export const Email = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading email data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { metrics, changes } = data;

  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total List */}
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total List</p>
              <p className="text-3xl font-bold text-blue-700 mt-2">
                {metrics.totalList.toLocaleString()}
              </p>
              <div className="mt-2 flex items-center text-sm">
                <span className={`font-semibold ${
                  changes.totalList > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.totalList > 0 ? '↑' : '↓'} {Math.abs(changes.totalList).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-1">vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Gained */}
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gained</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                +{metrics.gained.toLocaleString()}
              </p>
              <div className="mt-2 flex items-center text-sm">
                <span className={`font-semibold ${
                  changes.gained > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.gained > 0 ? '↑' : '↓'} {Math.abs(changes.gained).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-1">vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Lost */}
        <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Lost</p>
              <p className="text-3xl font-bold text-orange-700 mt-2">
                -{metrics.lost.toLocaleString()}
              </p>
              <div className="mt-2 flex items-center text-sm">
                <span className={`font-semibold ${
                  changes.lost < 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.lost < 0 ? '↓' : '↑'} {Math.abs(changes.lost).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-1">vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingDown className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Unsubscribed */}
        <div className="bg-red-50 p-6 rounded-lg border border-red-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unsubscribed</p>
              <p className="text-3xl font-bold text-red-700 mt-2">
                {metrics.unsubscribed.toLocaleString()}
              </p>
              <div className="mt-2 flex items-center text-sm">
                <span className={`font-semibold ${
                  changes.unsubscribed < 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {changes.unsubscribed < 0 ? '↓' : '↑'} {Math.abs(changes.unsubscribed).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-1">vs last period</span>
              </div>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <UserMinus className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Net Growth Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Net Growth</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">
              {metrics.netGrowth > 0 ? '+' : ''}{metrics.netGrowth.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {metrics.gained.toLocaleString()} gained - {metrics.lost.toLocaleString()} lost
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-gray-600">Growth Rate</p>
            <p className="text-3xl font-bold text-green-700 mt-1">
              {metrics.growthRate.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Month over month
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

Email.displayName = 'Email';